<template>
  <section v-if="loading || picks.length" class="todays-picks">
    <header class="todays-picks__head">
      <h2
        class="todays-picks__heading text-head-2 text-sora text-bold mobile-font--h4"
      >
        {{ $t("main.m_picks_heading") }}
      </h2>
      <a
        href="#"
        class="todays-picks__view-all todays-picks__view-all--head text-body-2"
        @click.prevent="navigateAll"
      >
        {{ $t("main.m_picks_view_all") }}
      </a>
    </header>

    <div class="todays-picks__viewport">
      <ul
        v-if="!loading"
        ref="gridRef"
        class="todays-picks__grid"
        :class="{ 'todays-picks__grid--dragging': isDragging }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @click.capture="onClickCapture"
        @scroll.passive="updateArrows"
      >
        <li
          v-for="estate in picks"
          :key="estate.id"
          class="todays-picks__cell"
        >
          <listing-card
            :estate="estate"
            variant="tile"
            property-type="estates"
            source="homepage"
          />
        </li>
      </ul>

      <ul v-else class="todays-picks__grid">
        <li v-for="i in 4" :key="i" class="todays-picks__cell">
          <q-skeleton height="425px" width="100%" />
        </li>
      </ul>

      <button
        v-show="!loading && canScrollPrev"
        type="button"
        class="todays-picks__arrow todays-picks__arrow--prev"
        :aria-label="$t('main.m_picks_prev')"
        @click="scrollByStep(-1)"
      >
        <base-icon name="base/ChevronLeft" size="20px" />
      </button>
      <button
        v-show="!loading && canScrollNext"
        type="button"
        class="todays-picks__arrow todays-picks__arrow--next"
        :aria-label="$t('main.m_picks_next')"
        @click="scrollByStep(1)"
      >
        <base-icon name="base/ChevronRight" size="20px" />
      </button>
    </div>

    <div
      v-if="!loading && picks.length > 1"
      class="todays-picks__dots"
      role="tablist"
      aria-label="Pagination"
    >
      <button
        v-for="(_, idx) in picks"
        :key="idx"
        type="button"
        class="todays-picks__dot"
        :class="{ 'todays-picks__dot--active': activeIndex === idx }"
        :aria-label="`Go to card ${idx + 1}`"
        :aria-current="activeIndex === idx ? 'true' : undefined"
        @click="scrollToIndex(idx)"
      />
    </div>

    <a
      href="#"
      class="todays-picks__view-all todays-picks__view-all--foot text-body-2"
      @click.prevent="navigateAll"
    >
      {{ $t("main.m_picks_view_all") }}
    </a>
  </section>
</template>

<script setup lang="ts">
import { useEstatePool } from "~/composables/useEstatePool";
import type { UpdatedUnitResponseDTO } from "~/store/unitPage";

const localePath = useLocalePath();

const SELLING_FAST_MAX_AVAIL = 30;
const NEW_LISTING_DAYS = 60;

const { pool, loading, ensure } = useEstatePool();

const picks = computed<UpdatedUnitResponseDTO[]>(() => {
  if (!pool.value.length) return [];
  const used = new Set<string>();
  const result: UpdatedUnitResponseDTO[] = [];

  const pushIf = (estate: UpdatedUnitResponseDTO | undefined) => {
    if (!estate || used.has(estate.id)) return;
    used.add(estate.id);
    result.push(estate);
  };

  const byApr = [...pool.value]
    .filter((e) => typeof e.apr === "number" && e.apr > 0)
    .sort((a, b) => (b.apr ?? 0) - (a.apr ?? 0));
  pushIf(byApr[0]);

  const byEntry = [...pool.value]
    .filter(
      (e) =>
        typeof e.minimumInvestmentUSD === "number" &&
        (e.minimumInvestmentUSD ?? 0) > 0,
    )
    .sort(
      (a, b) =>
        (a.minimumInvestmentUSD ?? Infinity) -
        (b.minimumInvestmentUSD ?? Infinity),
    );
  pushIf(byEntry[0]);

  const bySellingFast = [...pool.value]
    .filter((e) => {
      const avail = e.tokenomics?.availableSupplyPercentage;
      return (
        typeof avail === "number" && avail > 0 && avail <= SELLING_FAST_MAX_AVAIL
      );
    })
    .sort(
      (a, b) =>
        (a.tokenomics?.availableSupplyPercentage ?? Infinity) -
        (b.tokenomics?.availableSupplyPercentage ?? Infinity),
    );
  pushIf(bySellingFast[0]);

  const dayMs = 24 * 60 * 60 * 1000;
  const cutoff = Date.now() - NEW_LISTING_DAYS * dayMs;
  const byNew = [...pool.value]
    .filter((e) => {
      const t = e.createdAt ? new Date(e.createdAt).getTime() : 0;
      return t > cutoff;
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime(),
    );
  pushIf(byNew[0]);

  return result;
});

const navigateAll = () => {
  navigateTo(localePath({ path: "/directory", query: { "order[apr]": "desc" } }));
};

const gridRef = ref<HTMLUListElement | null>(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const isDragging = ref(false);
const activeIndex = ref(0);

const DRAG_CLICK_THRESHOLD = 6;

let dragStartX = 0;
let dragStartScrollLeft = 0;
let dragPointerId: number | null = null;
let dragDistance = 0;
let dragActive = false;
let suppressNextClick = false;

const cellStep = () => {
  const el = gridRef.value;
  if (!el) return 0;
  const firstCell = el.querySelector<HTMLLIElement>(".todays-picks__cell");
  const gap = parseFloat(getComputedStyle(el).columnGap || "0") || 20;
  return firstCell
    ? firstCell.getBoundingClientRect().width + gap
    : el.clientWidth * 0.9;
};

const updateArrows = () => {
  const el = gridRef.value;
  if (!el) {
    canScrollPrev.value = false;
    canScrollNext.value = false;
    return;
  }
  const maxScroll = el.scrollWidth - el.clientWidth;
  canScrollPrev.value = el.scrollLeft > 1;
  canScrollNext.value = maxScroll > 1 && el.scrollLeft < maxScroll - 1;
  const step = cellStep();
  if (step > 0) {
    activeIndex.value = Math.min(
      Math.max(picks.value.length - 1, 0),
      Math.max(0, Math.round(el.scrollLeft / step))
    );
  }
};

const scrollByStep = (direction: 1 | -1) => {
  const el = gridRef.value;
  if (!el) return;
  el.scrollBy({ left: direction * cellStep(), behavior: "smooth" });
};

const scrollToIndex = (idx: number) => {
  const el = gridRef.value;
  if (!el) return;
  el.scrollTo({ left: idx * cellStep(), behavior: "smooth" });
};

const onPointerDown = (event: PointerEvent) => {
  if (event.pointerType === "touch") return;
  if (event.button !== 0) return;
  const el = gridRef.value;
  if (!el) return;
  if ((el.scrollWidth - el.clientWidth) <= 1) return;
  dragPointerId = event.pointerId;
  dragStartX = event.clientX;
  dragStartScrollLeft = el.scrollLeft;
  dragDistance = 0;
  dragActive = false;
  // NB: do NOT setPointerCapture here. Capturing on pointerdown retargets the
  // subsequent `click` to this <ul>, so clicks never reach the card links or the
  // Buy Tokens button underneath. Capture only once an actual drag starts below.
};

const onPointerMove = (event: PointerEvent) => {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  const el = gridRef.value;
  if (!el) return;
  const dx = event.clientX - dragStartX;
  dragDistance = Math.max(dragDistance, Math.abs(dx));
  if (!dragActive) {
    // Wait for a real drag before hijacking the pointer — a plain click stays
    // uncaptured and reaches the card/CTA normally.
    if (dragDistance <= DRAG_CLICK_THRESHOLD) return;
    dragActive = true;
    isDragging.value = true;
    el.setPointerCapture(event.pointerId);
  }
  el.scrollLeft = dragStartScrollLeft - dx;
};

const onPointerUp = (event: PointerEvent) => {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  const el = gridRef.value;
  if (el && el.hasPointerCapture(event.pointerId)) {
    el.releasePointerCapture(event.pointerId);
  }
  if (dragActive) suppressNextClick = true;
  dragPointerId = null;
  dragActive = false;
  isDragging.value = false;
};

const onClickCapture = (event: MouseEvent) => {
  if (!suppressNextClick) return;
  suppressNextClick = false;
  event.preventDefault();
  event.stopPropagation();
};

let resizeObserver: ResizeObserver | null = null;

watch(picks, () => nextTick(updateArrows), { flush: "post" });

onMounted(() => {
  ensure();
  nextTick(() => {
    updateArrows();
    if (gridRef.value && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateArrows());
      resizeObserver.observe(gridRef.value);
    }
  });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<style scoped lang="scss">
.todays-picks {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  &__head {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
  }

  &__eyebrow {
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: var(--font-micro);
  }

  &__heading {
    margin: 0;
    color: var(--text-primary);
    letter-spacing: -0.5px;
  }

  &__viewport {
    position: relative;
    width: 100%;
  }

  &__grid {
    list-style: none;
    width: calc(100% + 32px);
    margin: -16px;
    padding: 16px;
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    gap: 20px;
    cursor: grab;
    user-select: none;
    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    &--dragging {
      cursor: grabbing;
      scroll-behavior: auto;
    }
  }

  &__cell {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 320px;
    min-width: 0;
    max-width: 320px;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid var(--gray-monochrome-20, rgba(15, 23, 42, 0.08));
    background: #fff;
    color: var(--black-monochrome);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    padding: 0;
    &:hover {
      box-shadow: 0 8px 26px rgba(15, 23, 42, 0.18);
      transform: translateY(-50%) scale(1.05);
    }
    &:active {
      transform: translateY(-50%) scale(0.97);
    }
    &--prev {
      left: -12px;
    }
    &--next {
      right: -12px;
    }
  }

  &__view-all {
    flex-shrink: 0;
    color: var(--black-monochrome-72);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-color: var(--gray-monochrome-40);
    transition: color 0.2s ease, text-decoration-color 0.2s ease;
    &:hover {
      color: var(--black-monochrome);
      text-decoration-color: var(--black-monochrome);
    }

    &--foot {
      display: none;
      align-self: flex-start;
    }
  }

  /* Pagination dots only render on the mobile carousel where the arrows are
     hidden — desktop has visible prev/next affordances instead. */
  &__dots {
    display: none;
  }

  &__dot {
    appearance: none;
    border: none;
    background: var(--gray-monochrome-40);
    width: 6px;
    height: 6px;
    padding: 0;
    border-radius: 50%;
    cursor: pointer;
    transition:
      width 0.2s ease,
      border-radius 0.2s ease,
      background-color 0.2s ease;
    &--active {
      background: var(--black-monochrome-60);
      width: 16px;
      border-radius: 6px;
    }
  }
}

@media (max-width: 75rem) {
  .todays-picks {
    gap: 16px;
    &__grid {
      gap: 16px;
    }
    &__cell {
      flex: 0 0 280px;
      max-width: 280px;
    }
  }
}

@media (max-width: 32rem) {
  .todays-picks {
    &__cell {
      flex: 0 0 calc(100% - 8px);
      max-width: calc(100% - 8px);
    }
    &__arrow {
      display: none;
    }
    &__view-all--head {
      display: none;
    }
    &__view-all--foot {
      display: inline-flex;
    }
    &__dots {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 2px;
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .todays-picks__grid {
    cursor: auto;
  }
}
</style>
